/**
 * Task Service
 * Handles all task-related API operations
 * Based on Tim-Combo patterns - using Make_Authorized_API_Call
 */

import { Make_Authorized_API_Call, API_ENDPOINTS, isSuccessResponse } from './apiClient';

/**
 * Task Service API methods
 */
export const taskService = {
  /**
   * Get list of tasks with optional filters
   */
  async getTasks(filters = {}) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.LIST,
      'GET',
      undefined,
      { params: filters }
    );

    if (isSuccessResponse(response)) {
      // Validate response format - NO FALLBACKS that hide errors
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records; // Can be empty array if no results
    }
    throw new Error(response.message || 'Failed to fetch tasks');
  },

  /**
   * Get a single task by ID
   */
  async getTask(taskId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.GET(taskId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      // Validate response format - NO FALLBACKS that hide errors
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      // Return first record or null if empty (both are legitimate)
      return response.data.Records[0] || null;
    }
    throw new Error(response.message || 'Failed to fetch task');
  },

  /**
   * Create a new task
   */
  async createTask(taskData) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.CREATE,
      'POST',
      taskData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to create task');
  },

  /**
   * Update an existing task
   */
  async updateTask(taskId, updates) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.UPDATE(taskId),
      'PUT',
      updates
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update task');
  },

  /**
   * Delete a task
   */
  async deleteTask(taskId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.DELETE(taskId),
      'DELETE'
    );

    if (isSuccessResponse(response)) {
      return true;
    }
    throw new Error(response.message || 'Failed to delete task');
  },

  /**
   * Mark a task as complete
   */
  async completeTask(taskId, completionData = {}) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.COMPLETE(taskId),
      'POST',
      completionData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to complete task');
  },

  /**
   * Assign a task to a family member
   */
  async assignTask(taskId, assigneeId, notes = '') {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.ASSIGN(taskId),
      'POST',
      {
        assignee_id: assigneeId,
        notes
      }
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to assign task');
  },

  /**
   * Add a comment to a task
   */
  async addComment(taskId, comment) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.COMMENT(taskId),
      'POST',
      { comment }
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to add comment');
  },

  /**
   * Get task comments
   */
  async getComments(taskId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.COMMENT(taskId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch comments');
  },

  /**
   * Request to outsource a task
   */
  async outsourceTask(taskId, outsourceData) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.OUTSOURCE(taskId),
      'PUT',
      outsourceData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to outsource task');
  },

  /**
   * Get task history/audit trail
   */
  async getTaskHistory(taskId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.HISTORY(taskId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch task history');
  },

  /**
   * Upload attachment to task
   */
  async uploadAttachment(taskId, file) {
    // Get presigned URL first
    const urlResponse = await Make_Authorized_API_Call(
      API_ENDPOINTS.FILES.GET_UPLOAD_URL,
      'POST',
      {
        task_id: taskId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size
      }
    );

    if (!isSuccessResponse(urlResponse)) {
      throw new Error('Failed to get upload URL');
    }

    // Upload file to S3
    const uploadUrl = urlResponse.data?.upload_url;
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    // Register attachment with task
    const attachResponse = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.ATTACHMENTS(taskId),
      'POST',
      {
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        file_url: urlResponse.data?.file_url
      }
    );

    if (isSuccessResponse(attachResponse)) {
      return attachResponse.data?.Records?.[0] || attachResponse.data;
    }
    throw new Error('Failed to register attachment');
  },

  /**
   * Get aging priority for tasks
   */
  async getAgingPriority(familyId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.AGING_PRIORITY,
      'GET',
      undefined,
      { params: { family_id: familyId } }
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch aging priorities');
  },

  /**
   * Bulk update multiple tasks
   */
  async bulkUpdateTasks(taskIds, updates) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.BULK_UPDATE,
      'POST',
      {
        task_ids: taskIds,
        updates
      }
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to bulk update tasks');
  },

  /**
   * Get contractor recommendations for a task
   */
  async getContractorRecommendations(taskId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.CONTRACTORS.RECOMMEND(taskId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch contractor recommendations');
  }
};

/**
 * Event Service API methods
 * Handles hierarchical task events
 */
export const eventService = {
  /**
   * Get list of events with optional filters
   */
  async getEvents(filters = {}) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.LIST,
      'GET',
      undefined,
      { params: filters }
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch events');
  },

  /**
   * Get a single event by ID
   */
  async getEvent(eventId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.GET(eventId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records[0] || null;
    }
    throw new Error(response.message || 'Failed to fetch event');
  },

  /**
   * Create a new event
   */
  async createEvent(eventData) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.CREATE,
      'POST',
      eventData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to create event');
  },

  /**
   * Update an existing event
   */
  async updateEvent(eventId, updates) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.UPDATE(eventId),
      'PUT',
      updates
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update event');
  },

  /**
   * Delete an event
   */
  async deleteEvent(eventId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.DELETE(eventId),
      'DELETE'
    );

    if (isSuccessResponse(response)) {
      return true;
    }
    throw new Error(response.message || 'Failed to delete event');
  },

  /**
   * Get event progress (completion percentage, task counts)
   */
  async getEventProgress(eventId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.PROGRESS(eventId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to fetch event progress');
  },

  /**
   * Get all tasks under an event
   */
  async getEventTasks(eventId) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.TASKS(eventId),
      'GET'
    );

    if (isSuccessResponse(response)) {
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records;
    }
    throw new Error(response.message || 'Failed to fetch event tasks');
  },

  /**
   * Add a task to an event
   */
  async addTaskToEvent(eventId, taskData) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.EVENTS.ADD_TASK(eventId),
      'POST',
      taskData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to add task to event');
  }
};

export default taskService;